import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { ButtonSpinner } from '../../components/ui/Loader';
import {
  useGetEmailSettingsQuery,
  useUpdateEmailSettingsMutation,
  useTestEmailSettingsMutation,
} from '../../store/api/adminApi';

export function AdminEmailSettingsPage() {
  const { data, isLoading, refetch } = useGetEmailSettingsQuery();
  const [updateSettings, { isLoading: isSaving }] = useUpdateEmailSettingsMutation();
  const [testEmail, { isLoading: isTesting }] = useTestEmailSettingsMutation();

  const settings = data?.data;

  const [form, setForm] = useState({
    enabled: true,
    provider: 'gmail' as 'gmail' | 'smtp',
    host: 'smtp.gmail.com',
    port: 587,
    secure: false,
    user: '',
    password: '',
    fromEmail: '',
    fromName: '',
    dailyLimit: 500,
  });

  useEffect(() => {
    if (settings) {
      setForm((prev) => ({
        ...prev,
        enabled: true,
        provider: settings.provider,
        host: settings.host,
        port: settings.port,
        secure: settings.secure,
        user: settings.user,
        fromEmail: settings.fromEmail,
        fromName: settings.fromName,
        dailyLimit: settings.dailyLimit,
        password: '',
      }));
    }
  }, [settings]);

  const handleSave = async () => {
    try {
      const payload: Record<string, unknown> = { ...form, enabled: true };
      delete payload.dailyLimit;
      if (!payload.password) delete payload.password;
      await updateSettings(payload).unwrap();
      toast.success('تم حفظ إعدادات الإيميل');
      refetch();
    } catch (err) {
      const error = err as { data?: { message?: string } };
      toast.error(error?.data?.message || 'فشل الحفظ');
    }
  };

  const handleTest = async () => {
    try {
      await testEmail({}).unwrap();
      toast.success('تم إرسال إيميل تجريبي — تحقق من صندوق الوارد');
    } catch (err) {
      const error = err as { data?: { message?: string } };
      toast.error(error?.data?.message || 'فشل إرسال الإيميل التجريبي');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ButtonSpinner />
      </div>
    );
  }

  const quotaPercent = settings?.quotaPercent ?? 0;
  const quotaColor = quotaPercent >= 100 ? 'bg-red-500' : quotaPercent >= 80 ? 'bg-amber-500' : 'bg-brand-accent';
  const canTest =
    Boolean(settings?.canTest) ||
    Boolean(settings?.hasPassword && (form.user || settings?.user)) ||
    Boolean(form.password && form.user);

  return (
    <div className="min-h-screen animate-slide-up max-w-3xl mx-auto px-4 pb-12">
      <div className="mb-8 text-center">
        <h1 className="text-3xl md:text-4xl font-heading font-bold text-brand-accent mb-2">
          إعدادات الإيميل
        </h1>
        <p className="text-neutral-medium">
          اضبط Gmail أو SMTP لإرسال أكواد التحقق لزبائنك
        </p>
      </div>

      {/* Quota banner */}
      <div className="card-glass rounded-xl p-5 mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="font-body text-neutral-medium">الاستخدام اليومي</span>
          <span className="font-mono text-brand-accent">
            {settings?.emailsSentToday ?? 0} / 500
          </span>
        </div>
        <div className="w-full bg-neutral-dark/30 rounded-full h-3 overflow-hidden">
          <div className={`h-full ${quotaColor} transition-all`} style={{ width: `${Math.min(quotaPercent, 100)}%` }} />
        </div>
        {quotaPercent >= 80 && (
          <p className="text-amber-400 text-sm mt-2">
            {quotaPercent >= 100 ? 'انتهت الحصة اليومية — الزبائن لن يستلموا أكواد حتى منتصف الليل' : 'اقتربت من نهاية الحصة اليومية'}
          </p>
        )}
        {!settings?.configured && (
          <p className="text-red-400 text-sm mt-2">
            ⚠️ الإيميل غير مضبوط — عند محاولة التسجيل سيظهر للزبون خطأ عام وستصلك تنبيه على التلغرام
          </p>
        )}
      </div>

      <div className="card-glass rounded-xl p-6 space-y-5">
        <div className="flex items-center gap-3 rounded-lg border border-green-500/30 bg-green-500/10 px-4 py-3">
          <span className="text-green-400 text-lg">✓</span>
          <div>
            <div className="font-body text-green-300">إرسال الإيميل مفعّل دائماً من المنصة</div>
            <div className="text-xs text-neutral-medium mt-0.5">اضبط البريد وApp Password أدناه — لا يمكن إيقاف الإرسال من هنا</div>
          </div>
        </div>

        <div>
          <label className="block text-sm text-neutral-medium mb-1">نوع المزود</label>
          <select
            value={form.provider}
            onChange={(e) => setForm({ ...form, provider: e.target.value as 'gmail' | 'smtp' })}
            className="w-full bg-brand-primary/20 border border-neutral-dark/40 rounded-lg px-4 py-2 font-body"
          >
            <option value="gmail">Gmail (مجاني ~500/يوم)</option>
            <option value="smtp">SMTP مخصص</option>
          </select>
        </div>

        {form.provider === 'smtp' && (
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm text-neutral-medium mb-1">SMTP Host</label>
              <input
                value={form.host}
                onChange={(e) => setForm({ ...form, host: e.target.value })}
                className="w-full bg-brand-primary/20 border border-neutral-dark/40 rounded-lg px-4 py-2"
              />
            </div>
            <div>
              <label className="block text-sm text-neutral-medium mb-1">Port</label>
              <input
                type="number"
                value={form.port}
                onChange={(e) => setForm({ ...form, port: Number(e.target.value) })}
                className="w-full bg-brand-primary/20 border border-neutral-dark/40 rounded-lg px-4 py-2"
              />
            </div>
            <label className="flex items-center gap-2 self-end pb-2">
              <input type="checkbox" checked={form.secure} onChange={(e) => setForm({ ...form, secure: e.target.checked })} />
              SSL/TLS
            </label>
          </div>
        )}

        <div>
          <label className="block text-sm text-neutral-medium mb-1">البريد الإلكتروني (Gmail)</label>
          <input
            type="email"
            value={form.user}
            onChange={(e) => setForm({ ...form, user: e.target.value, fromEmail: e.target.value || form.fromEmail })}
            placeholder="shop@gmail.com"
            className="w-full bg-brand-primary/20 border border-neutral-dark/40 rounded-lg px-4 py-2"
            dir="ltr"
          />
        </div>

        <div>
          <label className="block text-sm text-neutral-medium mb-1">
            App Password {settings?.hasPassword && <span className="text-green-400">(محفوظ)</span>}
          </label>
          <input
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="xxxx xxxx xxxx xxxx"
            className="w-full bg-brand-primary/20 border border-neutral-dark/40 rounded-lg px-4 py-2"
            dir="ltr"
          />
          <p className="text-xs text-neutral-medium mt-1">
            Google Account → Security → 2-Step Verification → App Passwords
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-neutral-medium mb-1">اسم المرسل (يظهر للزبون)</label>
            <input
              value={form.fromName}
              onChange={(e) => setForm({ ...form, fromName: e.target.value })}
              placeholder="متجري"
              className="w-full bg-brand-primary/20 border border-neutral-dark/40 rounded-lg px-4 py-2"
            />
          </div>
          <div>
            <label className="block text-sm text-neutral-medium mb-1">الحد اليومي</label>
            <div className="w-full bg-brand-primary/20 border border-neutral-dark/40 rounded-lg px-4 py-2 text-neutral-medium flex items-center justify-between">
              <span className="font-mono text-brand-accent">500</span>
              <span className="text-xs">ثابت من المنصة — لا يمكن تغييره</span>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 pt-4">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="btn-primary px-6 py-2 rounded-lg font-body flex items-center gap-2"
          >
            {isSaving && <ButtonSpinner size="sm" />}
            حفظ الإعدادات
          </button>
          <button
            onClick={handleTest}
            disabled={isTesting || !canTest}
            className="px-6 py-2 rounded-lg border border-brand-accent text-brand-accent font-body hover:bg-brand-accent/10 disabled:opacity-40 disabled:cursor-not-allowed"
            title={!canTest ? 'احفظ البريد وApp Password أولاً' : undefined}
          >
            {isTesting ? 'جاري الإرسال...' : 'إرسال إيميل تجريبي'}
          </button>
        </div>
      </div>
    </div>
  );
}
